import wait from '../utils/wait';

const API_URL = process.env.REACT_APP_API_URL;

const base = (envId) => `${API_URL}/api/environments/${envId}/flows/generation`;

const POLL_START_MS = 1500;
const POLL_MAX_MS = 3000;
const POLL_STEP_MS = 300;

const jobStorageKey = (envId) => `flowgate.generationJob.${envId}`;

/**
 * Turn a failed generation response into something worth showing a person.
 *
 * The server sends ProblemDetail, and its `detail` is written for the user ("You have reached
 * today's limit"). A raw status code in the chat would be noise, so anything without a detail
 * falls back to plain language rather than leaking the transport.
 */
async function failure(response, fallback) {
  const body = await response.json().catch(() => null);
  return new Error(body?.detail || body?.message || fallback);
}

/** What survives a page navigation: enough to find the job again, nothing about its content. */
function saveJob(envId, jobId) {
  try {
    sessionStorage.setItem(jobStorageKey(envId), JSON.stringify({ jobId }));
  } catch {
    // Private browsing / storage disabled: the job still finishes, it just cannot be recovered
    // after a navigation. Never worth failing the generation itself over.
  }
}

function clearJob(envId) {
  try {
    sessionStorage.removeItem(jobStorageKey(envId));
  } catch {
    // See saveJob, storage access failing here is not this call's problem to solve.
  }
}

function peekJob(envId) {
  try {
    const raw = sessionStorage.getItem(jobStorageKey(envId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Poll a job every 1.5s, backing off to 3s, until it succeeds or fails.
 *
 * No single request is ever open longer than one poll interval, the opposite of the bug this
 * replaces, where one HTTP request stayed open for the whole 2-4 minute generation and CloudFront
 * cut it off at 60s.
 */
async function pollJob(envId, jobId, authenticatedFetch, onProgress) {
  let interval = POLL_START_MS;
  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const response = await authenticatedFetch(`${base(envId)}/jobs/${jobId}`);
      if (!response.ok) {
        throw await failure(response, 'Could not check on that flow. Try describing it again.');
      }
      const job = await response.json();
      if (onProgress) onProgress(job);

      if (job.status === 'SUCCEEDED') {
        clearJob(envId);
        return job.result;
      }
      if (job.status === 'FAILED') {
        clearJob(envId);
        throw new Error(job.error || 'That flow could not be generated. Try describing it again.');
      }

      await wait(interval);
      interval = Math.min(interval + POLL_STEP_MS, POLL_MAX_MS);
    }
  } catch (error) {
    clearJob(envId);
    throw error;
  }
}

const flowGenerationService = {
  /**
   * Whether generation is configured at all. False on a server with no API key, and the UI
   * hides the whole mode rather than offering a button that always fails.
   */
  async isAvailable(envId, authenticatedFetch) {
    try {
      const response = await authenticatedFetch(`${base(envId)}/availability`);
      if (!response.ok) return false;
      const body = await response.json();
      return body.available === true;
    } catch {
      // Unreachable is indistinguishable from unconfigured from here, and both mean the same
      // thing to the user: this mode is not usable right now.
      return false;
    }
  },

  /**
   * First turn: describe the flow you want. Starts a background job and resolves once it
   * succeeds, with the same shape the old synchronous endpoint returned, the caller does not
   * need to know generation moved off the request thread.
   *
   * `flowJson` is what is already on the canvas, and the first turn is exactly when it is most
   * likely to be something: opening a template puts a whole flow there before a word is typed.
   *
   * `onProgress`, when given, is called with `{ status, stage, progressNote }` on every poll,
   * real progress from the server, not a guess from an elapsed timer.
   */
  async generate(envId, { prompt, businessContext, useAssets, flowJson }, authenticatedFetch, onProgress) {
    const response = await authenticatedFetch(base(envId), {
      method: 'POST',
      body: JSON.stringify({ prompt, businessContext, useAssets: useAssets !== false, flowJson }),
    });
    if (!response.ok) {
      throw await failure(response, 'That flow could not be generated. Try describing it again.');
    }
    const { jobId } = await response.json();
    saveJob(envId, jobId);
    return pollJob(envId, jobId, authenticatedFetch, onProgress);
  },

  /**
   * Every turn after the first. Same job-and-poll shape as {@link generate}.
   *
   * `flowJson` is the flow as it stands NOW, and passing it matters: the server otherwise
   * replays its own record of the conversation, which knows nothing about edits made by hand
   * in the manual builder and would silently undo them.
   */
  async refine(envId, sessionId, { instruction, useAssets, flowJson }, authenticatedFetch, onProgress) {
    const response = await authenticatedFetch(`${base(envId)}/${sessionId}`, {
      method: 'POST',
      body: JSON.stringify({ instruction, useAssets: useAssets !== false, flowJson }),
    });
    if (!response.ok) {
      throw await failure(response, 'That change could not be applied. Try describing it again.');
    }
    const { jobId } = await response.json();
    saveJob(envId, jobId);
    return pollJob(envId, jobId, authenticatedFetch, onProgress);
  },

  /**
   * The conversations behind a saved flow, newest first: `{ sessionId, title, startedAt,
   * lastTurnAt, turns, producedFlow }`. Empty for a flow no conversation has been linked to.
   */
  async listSessions(envId, flowId, authenticatedFetch) {
    const response = await authenticatedFetch(`${API_URL}/api/environments/${envId}/flows/${flowId}/generation/sessions`);
    if (!response.ok) throw await failure(response, 'Could not load earlier conversations.');
    return response.json();
  },

  /** One conversation's transcript: `{ sessionId, startedAt, lastTurnAt, messages: [{ role, text, at }] }`. */
  async getSession(envId, flowId, sessionId, authenticatedFetch) {
    const response = await authenticatedFetch(`${API_URL}/api/environments/${envId}/flows/${flowId}/generation/sessions/${sessionId}`);
    if (!response.ok) throw await failure(response, 'Could not load that conversation.');
    return response.json();
  },

  /**
   * Tie a conversation to the flow it is about. Idempotent; called after a save and after every
   * turn on a saved flow, so the history survives whichever order those happen in. Never fails
   * the caller: a missed link is a gap in history, not a lost flow.
   */
  async linkSession(envId, flowId, sessionId, authenticatedFetch) {
    try {
      const response = await authenticatedFetch(`${API_URL}/api/environments/${envId}/flows/${flowId}/generation/sessions/${sessionId}`, { method: 'PUT' });
      return response.ok;
    } catch {
      return false;
    }
  },

  /**
   * Whether a job for this environment was left running when the pane last unmounted, the tab
   * navigated away and back, for instance. Peeking (rather than resuming outright) lets a caller
   * decide what to show before committing to it.
   */
  hasPendingJob(envId) {
    return peekJob(envId) !== null;
  },

  /**
   * Pick a pending job back up and keep polling it, or null if there is nothing to resume.
   * Resolves/rejects exactly like {@link generate}.
   */
  resumePendingJob(envId, authenticatedFetch, onProgress) {
    const pending = peekJob(envId);
    if (!pending) return null;
    return pollJob(envId, pending.jobId, authenticatedFetch, onProgress);
  },
};

export default flowGenerationService;
